// Moved from `src/hooks/mutation/useMutateDeleteTeam.ts` (now the app delegate)
// with the OT4 mutation slice (ARCFE-1033).
//
// Severed per ARCFE-921: the destructive toast and the Rematch
// `analyticsScribe.trackDeleteTeam` dispatch. The `onMutate` optimistic write
// (cancel team details, blank the joined-teams cache) stays in the package —
// it touches only workspace-owned cache identities.
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  GET_JOINED_TEAMS,
  GET_TEAM_DETAILS,
  type JoinedTeamsResponse,
} from '../../contracts';
import { deleteTeam } from '../api/teams';
import { isTransportError, type WorkspaceHttpError } from '../config';

export const DELETE_TEAM = 'deleteTeam';

export interface UseMutateDeleteTeamOptions {
  /** App-reaction port: fired where the Rematch `trackDeleteTeam` fired. */
  onTeamDeleted?: (teamId: string) => void;
  /** App-reaction port: fired where the legacy destructive toast fired. */
  onDeleteError?: (error: WorkspaceHttpError) => void;
}

/** DELETE /teams/{team_id} */
export function useMutateDeleteTeam({
  teamId,
  ...options
}: { teamId: string } & UseMutateDeleteTeamOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [DELETE_TEAM, { teamId }],
    mutationFn: async () => {
      return await deleteTeam(teamId);
    },
    onMutate: () => {
      void queryClient.cancelQueries({ queryKey: [GET_TEAM_DETAILS, { teamId }] });
      void queryClient.setQueryData<JoinedTeamsResponse>(
        [GET_JOINED_TEAMS],
        (oldData) => {
          if (!oldData) return oldData;
          return {
            joined_teams: [],
          };
        }
      );
    },
    onSuccess: () => {
      options?.onTeamDeleted?.(teamId);
    },
    onError: (error) => {
      if (isTransportError(error)) {
        options?.onDeleteError?.(error);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: [GET_JOINED_TEAMS],
      });
    },
  });
}
